import React, { useState } from 'react';
import { Box, Card, Typography, Avatar, Button, Stack, Chip } from '@mui/material';
import { ArrowRight as ArrowRightIcon } from '@mui/icons-material';

const PriorityQueue = ({ items = [], onViewAll, onItemAction, onItemCheckbox }) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const getItemCategory = (item) => {
    if (item.category) {
      if (item.category === 'critical') return 'critical';
      if (item.category === 'important') return 'important';
      if (item.category === 'optimization') return 'optimization';
    }
    if (item.status === 'Ranking gefallen') return 'critical';
    if (item.status === 'Einrichtung offen' || item.status === 'Bewertung offen') return 'important';
    if (item.status === 'Sichtbarkeit') return 'optimization';
    if (item.status === 'Abgeschlossen' || item.completedAt) return 'completed';
    return 'other';
  };

  const tabs = [
    { label: 'Kritisch', key: 'critical', count: items.filter(i => getItemCategory(i) === 'critical').length },
    { label: 'Wichtig', key: 'important', count: items.filter(i => getItemCategory(i) === 'important').length },
    { label: 'Optimierung', key: 'optimization', count: items.filter(i => getItemCategory(i) === 'optimization').length },
    { label: 'Abgeschlossen', key: 'completed', count: items.filter(i => getItemCategory(i) === 'completed').length }
  ];

  const filteredItems = items.filter(item => {
    const category = getItemCategory(item);
    if (selectedTab === 0) return category === 'critical';
    if (selectedTab === 1) return category === 'important';
    if (selectedTab === 2) return category === 'optimization';
    if (selectedTab === 3) return category === 'completed';
    return false;
  });

  const displayItems = filteredItems;

  const getStatusAndButtonText = (item) => {
    const actionType = item.actionType || '';
    const actionTypeLC = actionType.toLowerCase();
    const detail = (item.detail || '').toLowerCase();

    // Check detail text first to catch service-related issues - Purple
    if (detail.includes('service')) {
      return {
        status: 'Service hinzufügen',
        buttonText: 'Inhalte',
        colors: { bg: '#EFE7F5', text: '#7C5299' }
      };
    }

    // Check actionType for specific tag type
    // Ranking-related actions - Red
    if (actionTypeLC.includes('ranking') || actionTypeLC.includes('rank')) {
      return {
        status: 'Ranking gefallen',
        buttonText: 'Sichtbarkeit',
        colors: { bg: '#F5D5D8', text: '#A0384D' }
      };
    }

    // Media-related actions - Teal
    if (actionTypeLC.includes('media')) {
      return {
        status: 'Media erforderlich',
        buttonText: 'Inhalte',
        colors: { bg: '#E0F7FA', text: '#0097A7' }
      };
    }

    // Posts/Content-related actions - Orange
    if (actionTypeLC.includes('post') || actionTypeLC.includes('content') || actionTypeLC.includes('plan')) {
      return {
        status: 'Kein Plan aktiv',
        buttonText: 'Inhalte',
        colors: { bg: '#F9E8C4', text: '#C87F1E' }
      };
    }

    // Keyword/Setup-related actions - Blue
    if (actionTypeLC.includes('keyword') || actionTypeLC.includes('setup')) {
      return {
        status: 'Einrichtung offen',
        buttonText: 'Onboarding',
        colors: { bg: '#D9F0F7', text: '#4D87C2' }
      };
    }

    // Reviews/Answers - Green
    if (actionTypeLC.includes('review') || actionTypeLC.includes('answer') || actionTypeLC.includes('bewertung')) {
      return {
        status: 'Bewertung offen',
        buttonText: 'Antworten',
        colors: { bg: '#D7EEE5', text: '#1F7A5F' }
      };
    }

    // Fallback based on status field or category
    const statusLC = (item.status || '').toLowerCase();
    if (statusLC.includes('ranking')) {
      return {
        status: 'Ranking gefallen',
        buttonText: 'Sichtbarkeit',
        colors: { bg: '#F5D5D8', text: '#A0384D' }
      };
    }
    if (statusLC.includes('media')) {
      return {
        status: 'Media erforderlich',
        buttonText: 'Inhalte',
        colors: { bg: '#E0F7FA', text: '#0097A7' }
      };
    }
    if (statusLC.includes('plan')) {
      return {
        status: 'Kein Plan aktiv',
        buttonText: 'Inhalte',
        colors: { bg: '#F9E8C4', text: '#C87F1E' }
      };
    }
    if (statusLC.includes('einrichtung')) {
      return {
        status: 'Einrichtung offen',
        buttonText: 'Onboarding',
        colors: { bg: '#D9F0F7', text: '#4D87C2' }
      };
    }
    if (statusLC.includes('bewertung') || statusLC.includes('review')) {
      return {
        status: 'Bewertung offen',
        buttonText: 'Antworten',
        colors: { bg: '#D7EEE5', text: '#1F7A5F' }
      };
    }

    return {
      status: item.status || 'Offen',
      buttonText: 'Zeigen',
      colors: { bg: '#F2F1F3', text: '#3A3A40' }
    };
  };

  return (
    <Card
      sx={{
        background: '#FFFFFF',
        border: '1px solid #ECEBEE',
        borderRadius: '18px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}
    >
      {/* Header with Title and Tabs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#0A0A0B',
              fontFamily: 'Plus Jakarta Sans, system-ui',
              mb: 0.5
            }}
          >
            Was heute zählt
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#55555D',
              fontFamily: 'Plus Jakarta Sans, system-ui'
            }}
          >
            Priorisierte Konten – die größte Wirkung zuerst.
          </Typography>
        </Box>

        {/* Tabs - Button Group Style - In Header */}
        <Box
          sx={{
            display: 'inline-flex',
            gap: '12px',
            padding: '8px',
            backgroundColor: '#F7F6F3',
            borderRadius: '12px',
            flexShrink: 0,
            overflowX: 'auto',
            maxWidth: '400px',
            '&::-webkit-scrollbar': {
              height: '4px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#D0CED6',
              borderRadius: '2px',
              '&:hover': {
                backgroundColor: '#B8B5C0'
              }
            }
          }}
        >
          {tabs.map((tab, idx) => (
            <Button
              key={tab.key}
              onClick={() => setSelectedTab(idx)}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: selectedTab === idx ? 600 : 500,
                color: selectedTab === idx ? '#0A0A0B' : '#55555D',
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: selectedTab === idx ? '#FFFFFF' : 'transparent',
                border: selectedTab === idx ? '1px solid #ECEBEE' : 'none',
                fontFamily: 'Plus Jakarta Sans, system-ui',
                transition: 'all 120ms',
                '&:hover': {
                  backgroundColor: selectedTab === idx ? '#FFFFFF' : '#F2F1F3'
                }
              }}
            >
              {tab.label} {tab.count > 0 && `(${tab.count})`}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Empty State */}
      {displayItems.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: '#55555D', fontSize: '14px', fontFamily: 'Plus Jakarta Sans, system-ui' }}>
            Keine Einträge in dieser Kategorie
          </Typography>
        </Box>
      )}

      {/* Items List - Scrollable */}
      <Box
        sx={{
          maxHeight: '520px',
          overflowY: 'auto',
          paddingRight: '8px',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D0CED6',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#B8B5C0'
            }
          }
        }}
      >
        <Stack gap="16px">
        {displayItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#F7F6F3',
              borderRadius: '12px',
              transition: 'all 120ms',
              '&:hover': {
                backgroundColor: '#F2F1F3',
                '& .action-button': {
                  borderColor: '#C80F2E',
                  color: '#C80F2E'
                }
              }
            }}
          >
            {/* Avatar */}
            <Avatar
              sx={{
                width: 56,
                height: 56,
                fontSize: '13px',
                fontWeight: 700,
                backgroundColor: '#F0F0F3',
                color: '#0A0A0B',
                fontFamily: 'Plus Jakarta Sans, system-ui',
                border: '1px solid #ECEBEE',
                flexShrink: 0
              }}
            >
              {item.initials || 'XX'}
            </Avatar>

            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
              {/* Name + Status Badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#0A0A0B',
                    fontFamily: 'Plus Jakarta Sans, system-ui'
                  }}
                >
                  {item.businessName || 'Unbenannt'}
                </Typography>
                {(() => {
                  const { status, colors } = getStatusAndButtonText(item);
                  return (
                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        height: '22px',
                        fontSize: '11px',
                        fontWeight: 700,
                        backgroundColor: colors.bg,
                        color: colors.text,
                        borderRadius: '999px'
                      }}
                    />
                  );
                })()}
              </Box>

              {/* Detail Text */}
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#55555D',
                  fontFamily: 'Plus Jakarta Sans, system-ui',
                  lineHeight: 1.4
                }}
              >
                {item.detail || 'Keine Details'}
              </Typography>
            </Box>

            {/* Action Button */}
            <Button
              className="action-button"
              variant="outlined"
              endIcon={<ArrowRightIcon sx={{ fontSize: '14px' }} />}
              onClick={() => onItemAction?.(item)}
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#55555D',
                border: '1px solid #ECEBEE',
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                padding: '8px 14px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 120ms',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#C80F2E',
                  color: '#C80F2E'
                }
              }}
            >
              {getStatusAndButtonText(item).buttonText}
            </Button>
          </Box>
        ))}
        </Stack>
      </Box>

      {/* Footer Button */}
      {filteredItems.length > 0 && (
        <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #ECEBEE' }}>
          <Button
            onClick={onViewAll}
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#C80F2E',
              textTransform: 'none',
              padding: '10px',
              '&:hover': {
                backgroundColor: '#FDF2F4'
              }
            }}
          >
            Alle {filteredItems.length} Unternehmen ansehen →
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default PriorityQueue;
